/**
 * How long a file the browser cannot read takes to reach the server, said before it is sent: at the rate this
 * browser's last large send had (remembered here), or, never sent yet, at two common upload speeds.
 */
import { t } from './i18n.js';

const RATE_KEY = 'airpost.archive.sendRate';
/** A send smaller than this says little about the line (its first seconds weigh too much): it is not remembered. */
export const RATE_MIN_BYTES = 64_000_000;
/** The upload speeds (Mbit/s) a send is reckoned at when this browser has not sent a large file yet. */
export const REF_MBPS = [100, 50] as const;

/** Bytes per second of a line of `mbps` megabits. */
export const mbpsRate = (mbps: number): number => (mbps * 1_000_000) / 8;

/** Whole minutes a send of `bytes` takes at `rate` bytes/s (0 = under a minute). */
export const sendMinutes = (bytes: number, rate: number): number => Math.round(bytes / rate / 60);

/** A send has ended: its rate is kept for the next estimate, when it was large enough to tell. */
export function rememberSendRate(bytes: number, seconds: number): void {
  if (bytes < RATE_MIN_BYTES || !(seconds > 0)) return;
  try {
    window.localStorage.setItem(RATE_KEY, String(Math.round(bytes / seconds)));
  } catch {
    // storage refused (a private window): the next estimate uses the common speeds
  }
}

/** The rate (bytes/s) of this browser's last large send, or null. */
export function lastSendRate(): number | null {
  try {
    const v = Number(window.localStorage.getItem(RATE_KEY));
    return Number.isFinite(v) && v > 0 ? v : null;
  } catch {
    return null;
  }
}

/** "9 λεπτά", "1 ώρα 20 λεπτά", "λιγότερο από 1 λεπτό". */
function minutesText(m: number): string {
  if (m < 1) return t('ltoUnderMinute');
  const h = Math.floor(m / 60);
  const rest = m % 60;
  const hours = h ? `${h} ${h === 1 ? t('ltoHour') : t('ltoHours')}` : '';
  const mins = rest ? `${rest} ${rest === 1 ? t('ltoMinute') : t('minutes')}` : '';
  return [hours, mins].filter(Boolean).join(' ');
}

/** How long the file takes to go up: at this browser's last large send, or at the common speeds. */
export function sendTimeText(bytes: number, rate: number | null): string {
  const about = (m: number) => (m < 1 ? '' : `${t('ltoAbout')} `);
  if (rate) {
    const m = sendMinutes(bytes, rate);
    return `${about(m)}${minutesText(m)} (${t('ltoAtLastSend')})`;
  }
  const ms = REF_MBPS.map((mbps) => sendMinutes(bytes, mbpsRate(mbps)));
  if (ms.every((m) => m < 1)) return t('ltoUnderMinute');
  return `${about(ms[0]!)}${REF_MBPS.map((mbps, i) => `${minutesText(ms[i]!)} ${t('ltoAtSpeed')} ${mbps} Mbit/s`).join(', ')}`;
}
